import { Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { ISrsFigures, ISrsPeriodFigures } from '../../../../shared/services/srs-report/srs-report.model'

const fmt = (value: number) => Number(value ?? 0).toLocaleString('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

/**
 * Scope 3 by GHG Protocol category for the current period and whichever comparatives exist, oldest on
 * the left like the scope table and the generated document. Categories with no recorded data show a
 * "Not reported" badge rather than zero: an absent number is a gap, not a claim of zero emissions. A
 * year whose Scope 3 was recorded as a single total (an archived year entered without a breakdown)
 * shows a dash in the category rows and its whole Scope 3 in the other row.
 */
export const SrsScope3Table: FunctionComponent<{ figures: ISrsFigures }> = ({ figures }) => {
  const current = figures.periods.current
  if (!current || !current.scope3.length) return null

  const periods = [figures.periods.base, figures.periods.previous, current].filter(Boolean) as ISrsPeriodFigures[]
  const hasCategories = (period: ISrsPeriodFigures) => period.scope3.length > 0
  const anySingleTotal = periods.some((period) => !hasCategories(period))
  const anyOther = periods.some((period) => period.otherScope3Tco2e > 0)

  const categoryCell = (period: ISrsPeriodFigures, number: number) => {
    if (!hasCategories(period)) return '—'
    const category = period.scope3.find((c) => c.number === number)
    return !category || category.status === 'NOT_REPORTED'
      ? <Chip size='small' color='warning' variant='outlined' label='Not reported' />
      : fmt(category.tco2e)
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size='small' sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell>GHG Protocol category</TableCell>
            {periods.map((period) => (
              <TableCell align='right' key={period.reportPeriodId}>{period.label} (tCO2e)</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {current.scope3.map((category) => (
            <TableRow key={category.number}>
              <TableCell>{category.number}. {category.label}</TableCell>
              {periods.map((period) => (
                <TableCell align='right' key={`${category.number}-${period.reportPeriodId}`}>
                  {categoryCell(period, category.number)}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {anyOther && (
            <TableRow>
              <TableCell>Other Scope 3 not broken down by category</TableCell>
              {periods.map((period) => (
                <TableCell align='right' key={`other-${period.reportPeriodId}`}>{fmt(period.otherScope3Tco2e)}</TableCell>
              ))}
            </TableRow>
          )}
          <TableRow>
            <TableCell sx={{ fontFamily: 'EffraMd' }}>Scope 3</TableCell>
            {periods.map((period) => (
              <TableCell align='right' sx={{ fontFamily: 'EffraMd' }} key={`total-${period.reportPeriodId}`}>
                {fmt(period.scopeThreeTotal)}
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
      <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
        &ldquo;Not reported&rdquo; means no data has been recorded for the category in that period; it
        is not a claim of zero emissions.
        {anySingleTotal && ' A dash marks a year whose Scope 3 was recorded as a single total without a category breakdown; that total is shown under other Scope 3.'}
      </Typography>
    </Box>
  )
}
